import { useMemo } from "react";
import { Chess } from "chess.js";
import { createVerifiedLine, type VerifiedLine } from "@/features/board/review/verified-line";
import { type TeachingFact } from "@/features/board/review/teaching-explainer";
import { mergeRelationshipEvidence, relationshipPresentation } from "@/features/board/relationship-evidence";
import { DEFAULT_REVIEW_BOARD_SETTINGS } from "@/features/board/review-board-settings";
import { OpeningReference } from "@/features/openings/reference";
import { Typography } from "@mui/material";
import {
  BoardEvidenceWheel,
  type BoardEvidenceItem,
  type BoardEvidenceFocus,
} from "@/shared/components/chessboard/BoardEvidenceWheel";
import { principlePresentation, type PrincipleSignal } from "@chessla/shared";
import type { AnalyzedMoveResult } from "@/shared/api/engine.api";
import { MotifBadge, PrincipleGlyph, ReviewEvidenceGlyph } from "@/shared/components";
import { isPatternGlyphKind } from "@/shared/components/PatternGlyph";
import { boardEvidenceMotifAnnotation, boardMoveDestination, type BoardMotifAnnotation } from "./BoardMotifBadge";
import { visiblePrincipleSignals } from "./principle-markers";

import type { WinningOutcomeMotif } from "../game-outcome";
import type { TemporaryMoveEvidence } from "./temporary-move-evidence";
import { motifDescription, tacticalDescription } from "../review-copy";

interface Props {
  relationships?: BoardEvidenceItem[];
  move: AnalyzedMoveResult | null;
  displayFen: string;
  orientation: "white" | "black";
  showMotifs: boolean;
  showTactics?: boolean;
  showPrinciples: boolean;
  includeOpponentEvidence?: boolean;
  outcomeMotif?: WinningOutcomeMotif | null;
  reviewDecision?: boolean;
  revealEvidence?: boolean;
  temporaryActive?: boolean;
  temporaryEvidence?: TemporaryMoveEvidence | null;
  onPlayVerifiedLine?: () => void;
  onActiveChange?: (focus: BoardEvidenceFocus | null) => void;
  bookReference?: string | null;
  verifiedLineOverride?: VerifiedLine | null;
  positionFacts?: { fen: string; facts: readonly TeachingFact[] };
  precedingMove?: {
    fen: string;
    displayFen: string;
    evidence: TemporaryMoveEvidence;
    bookReference?: string;
    facts?: readonly TeachingFact[];
  };
}
type EvidenceItem =
  | {
      kind: "motif";
      id: string;
      square: string;
      label: string;
      annotation: BoardMotifAnnotation;
      description?: string;
      highlightSquares?: string[];
    }
  | { kind: "principle"; id: string; square: string; label: string; signal: PrincipleSignal }
  | { kind: "critical" | "verified"; id: string; square: string; label: string };

const title = (value: string) => value.toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
const itemColor = (item: EvidenceItem) =>
  item.kind === "motif"
    ? item.annotation.color
    : item.kind === "principle" && item.signal.status === "applied"
      ? "primary.main"
      : "secondary.main";
const verifiedLine = (move: AnalyzedMoveResult | null): string[] =>
  move?.bestMoveSan ? [move.bestMoveSan, ...(move.bestContinuation ?? [])] : [];
const verifiedLineCopy = (line: readonly string[]) => {
  return line.length ? ` Verified line: ${line.join(" ")}.` : "";
};

const motifEvidenceDescription = (
  annotation: BoardMotifAnnotation,
  move: AnalyzedMoveResult | null,
  line: readonly string[],
) => {
  if (annotation.evidenceKind !== "tactical")
    return `${motifDescription(annotation.motif ?? "", move ?? undefined)}${verifiedLineCopy(line)}`;
  const targets = annotation.targetSquares ?? [];
  const targetCopy = targets.length ? ` ${targets.length === 1 ? "Target" : "Targets"}: ${targets.join(", ")}.` : "";
  return `${move?.san ? `${move.san}: ` : ""}${tacticalDescription(annotation.motif ?? "")}${targetCopy}${verifiedLineCopy(line)}`;
};

const reviewEvidenceDescription = (
  kind: "critical" | "verified",
  move: AnalyzedMoveResult | null,
  line: readonly string[],
) => {
  if (kind === "verified") return `Verified line: ${line.join(" ")}.`;
  const description =
    move && Number.isFinite(move.centipawnLoss)
      ? `${move.san} changed the evaluation by ${Math.round(move.centipawnLoss)} centipawns.`
      : `${move?.san ?? "This move"} was selected for focused review.`;
  return `${description}${verifiedLineCopy(line)}`;
};

const useBoardEvidenceItems = ({
  move,
  displayFen,
  showMotifs,
  showTactics = showMotifs,
  showPrinciples,
  includeOpponentEvidence = false,
  outcomeMotif = null,
  reviewDecision = false,
  revealEvidence = true,
  temporaryActive = false,
  temporaryEvidence = null,
  onPlayVerifiedLine,
  bookReference,
  verifiedLineOverride,
  positionFacts,
}: Props) => {
  const facts = useMemo(
    () => (positionFacts?.fen === displayFen ? positionFacts.facts : []),
    [positionFacts, displayFen],
  );
  const line = useMemo(() => {
    if (verifiedLineOverride !== undefined) {
      const bound =
        verifiedLineOverride?.fen === displayFen
          ? createVerifiedLine(displayFen, verifiedLineOverride.moves, verifiedLineOverride.depth)
          : null;
      if (!bound) return [];
      const position = new Chess(bound.fen);
      return bound.moves.map((uci) => position.move(uci).san);
    }
    return temporaryActive ? [] : verifiedLine(move);
  }, [displayFen, move, temporaryActive, verifiedLineOverride]);
  const evidenceMove = temporaryActive ? null : move;
  const signals = useMemo(
    () =>
      showPrinciples
        ? (temporaryEvidence?.principleSignals ??
          visiblePrincipleSignals(move, displayFen, revealEvidence, temporaryActive))
        : [],
    [displayFen, move, revealEvidence, showPrinciples, temporaryActive, temporaryEvidence],
  );
  const groups = useMemo(() => {
    const merged = new Map<string, EvidenceItem[]>();
    const append = (square: string, item: EvidenceItem) => merged.set(square, [...(merged.get(square) ?? []), item]);
    if (
      !revealEvidence ||
      (temporaryActive && !temporaryEvidence && !line.length && !facts.length) ||
      (!temporaryActive && !move)
    )
      return [];
    if (!temporaryActive && move && !move.isPlayerMove && !includeOpponentEvidence && !reviewDecision) return [];
    const moveSquare =
      temporaryEvidence?.destination ??
      (temporaryActive ? verifiedLineOverride?.moves[0]?.slice(0, 2) : move ? boardMoveDestination(move) : null);
    const outcomeAnnotation =
      showMotifs && !temporaryActive && outcomeMotif ? boardEvidenceMotifAnnotation([outcomeMotif]) : null;
    const tacticalMotifs = new Set(evidenceMove?.tacticalFindings?.map(({ motif }) => motif) ?? []);
    const motifAnnotations: Array<{ annotation: BoardMotifAnnotation; squares: string[] }> = [];
    if ((showMotifs || showTactics) && moveSquare) {
      const motifs =
        temporaryEvidence?.motifs ?? evidenceMove?.motifs.filter((motif) => !tacticalMotifs.has(motif)) ?? [];
      if (showMotifs)
        motifs.forEach((motif) => {
          const annotation = boardEvidenceMotifAnnotation([motif]);
          if (annotation && (showTactics || annotation.evidenceKind !== "tactical"))
            motifAnnotations.push({ annotation: { ...annotation, motif }, squares: [moveSquare] });
        });
      if (!temporaryEvidence && showTactics)
        evidenceMove?.tacticalFindings?.forEach((finding) => {
          const annotation = boardEvidenceMotifAnnotation([finding.motif]);
          if (!annotation) return;
          const targets = (finding.targetSquares ?? []).filter((square) => /^[a-h][1-8]$/.test(square));
          motifAnnotations.push({
            annotation: { ...annotation, motif: finding.motif, targetSquares: targets },
            squares: targets.length ? targets : [moveSquare],
          });
        });
    }
    const seenMotifs = new Set<string>();
    if (moveSquare && outcomeAnnotation) {
      append(moveSquare, {
        kind: "motif",
        id: `outcome:${outcomeAnnotation.title}`,
        square: moveSquare,
        label: outcomeAnnotation.title,
        annotation: { ...outcomeAnnotation, motif: outcomeMotif ?? undefined },
      });
      seenMotifs.add(`${outcomeAnnotation.title}:${moveSquare}`);
    }
    motifAnnotations.forEach(({ annotation, squares }) =>
      squares.forEach((square) => {
        const key = `${annotation.title}:${square}`;
        if (seenMotifs.has(key)) return;
        seenMotifs.add(key);
        append(square, {
          kind: "motif",
          id: `motif:${annotation.title}:${square}`,
          square,
          label: annotation.title,
          annotation,
        });
      }),
    );
    if (temporaryActive && showTactics)
      facts
        .filter((fact) => fact.kind !== "placement")
        .forEach((fact, index) => {
          const label =
            fact.kind === "fork"
              ? "Double attack"
              : fact.kind === "hanging"
                ? "Undefended target"
                : title(fact.kind.replaceAll("_", " "));
          const square = ["capture", "castle", "development"].includes(fact.kind)
            ? fact.squares.at(-1)
            : fact.kind === "pin" || fact.kind === "hanging"
              ? fact.squares[1]
              : fact.squares[0];
          if (!square) return;
          const annotation = boardEvidenceMotifAnnotation([fact.kind]) ?? {
            kind:
              fact.kind === "hanging"
                ? ("hanging_piece" as const)
                : isPatternGlyphKind(fact.kind)
                  ? fact.kind
                  : ("text" as const),
            label: fact.kind === "check" ? "+" : label[0],
            title: label,
            color: "info.main",
          };
          append(square, {
            kind: "motif",
            id: `position:${fact.kind}:${index}`,
            square,
            label,
            annotation: { ...annotation, title: label },
            description: `${fact.move}: ${label}. Structural observation at ${fact.squares.join(", ")}; no material gain is implied.`,
            highlightSquares: fact.kind === "pin" ? [square] : fact.squares,
          });
        });
    if (moveSquare)
      signals.forEach((signal) => {
        const presentation = principlePresentation(signal.principle);
        append(moveSquare, {
          kind: "principle",
          id: `principle:${signal.principle}:${signal.status}`,
          square: moveSquare,
          label: `${signal.status === "missed" ? "Missed" : "Applied"} principle: ${title(presentation.name)}`,
          signal,
        });
      });
    if (moveSquare && reviewDecision)
      append(moveSquare, {
        kind: "critical",
        id: "critical",
        square: moveSquare,
        label: reviewEvidenceDescription("critical", evidenceMove, line),
      });
    if (
      moveSquare &&
      line.length > 1 &&
      (verifiedLineOverride || move?.bestContinuationScores?.length || move?.bestContinuationEvals?.length)
    ) {
      append(moveSquare, {
        kind: "verified",
        id: "verified",
        square: moveSquare,
        label: reviewEvidenceDescription("verified", evidenceMove, line),
      });
    }
    return [...merged].map(([square, items]) => ({ square, items }));
  }, [
    includeOpponentEvidence,
    move,
    outcomeMotif,
    revealEvidence,
    reviewDecision,
    showMotifs,
    showTactics,
    signals,
    temporaryActive,
    temporaryEvidence,
    verifiedLineOverride,
    line,
    evidenceMove,
    facts,
  ]);

  const items = useMemo<BoardEvidenceItem[]>(
    () =>
      groups.flatMap((group) =>
        group.items.map((item) => {
          const onSelect = line.length > 0 && !item.id.startsWith("position:") ? onPlayVerifiedLine : undefined;
          return {
            id: item.id,
            kind: item.kind,
            relationship:
              item.kind === "motif"
                ? item.annotation.motif === "hanging_piece"
                  ? "hanging"
                  : item.annotation.motif
                : undefined,
            square: item.square,
            label: item.label,
            color: itemColor(item),
            book: item.kind === "motif" && item.annotation.motif === "book",
            principleStatus: item.kind === "principle" ? item.signal.status : undefined,
            highlightSquares: item.kind === "motif" ? item.highlightSquares : undefined,
            highlightFen: item.kind === "motif" && item.highlightSquares ? displayFen : undefined,
            description:
              item.kind === "motif"
                ? (item.description ??
                  (item.annotation.motif === "book" && bookReference
                    ? bookReference
                    : motifEvidenceDescription(item.annotation, evidenceMove, line)))
                : item.kind === "principle"
                  ? principlePresentation(item.signal.principle).cue
                  : reviewEvidenceDescription(item.kind, evidenceMove, line),
            playable: Boolean(onSelect),
            onSelect,
            glyph:
              item.kind === "motif" ? (
                <MotifBadge kind={item.annotation.kind} label={item.annotation.label} color={item.annotation.color} />
              ) : item.kind === "principle" ? (
                <PrincipleGlyph principle={item.signal.principle} />
              ) : (
                <ReviewEvidenceGlyph kind={item.kind === "critical" ? "critical-moment" : "verified-line"} />
              ),
            disclosure:
              item.kind === "motif" ? (
                <>
                  <Typography variant="overline" color="secondary.main">
                    BOARD EVIDENCE
                  </Typography>
                  <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                    {item.annotation.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.description ??
                      (item.annotation.motif === "book" ? (
                        <OpeningReference name={bookReference} />
                      ) : (
                        motifEvidenceDescription(item.annotation, evidenceMove, line)
                      ))}
                  </Typography>
                </>
              ) : item.kind === "principle" ? (
                <>
                  <Typography
                    sx={{
                      color: item.signal.status === "missed" ? "secondary.main" : "primary.main",
                      fontSize: ".6rem",
                      fontWeight: 950,
                      letterSpacing: ".12em",
                    }}
                  >
                    {item.signal.status.toUpperCase()} PRINCIPLE
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: "1rem", fontWeight: 950, letterSpacing: "-.035em" }}>
                    {principlePresentation(item.signal.principle).name}
                  </Typography>
                  <Typography sx={{ mt: 0.3, color: "text.secondary", fontSize: ".66rem", fontWeight: 800 }}>
                    {principlePresentation(item.signal.principle).traditionalName}
                  </Typography>
                  <Typography sx={{ mt: 0.75, color: "text.secondary", fontSize: ".72rem", lineHeight: 1.45 }}>
                    {principlePresentation(item.signal.principle).cue}
                  </Typography>
                  {item.signal.alternativeSan && (
                    <Typography sx={{ mt: 0.75, fontSize: ".68rem", fontWeight: 800 }}>
                      VERIFIED ALTERNATIVE · {item.signal.alternativeSan}
                    </Typography>
                  )}
                  {item.signal.evaluationImpactCp != null && (
                    <Typography sx={{ mt: 0.25, fontSize: ".68rem" }}>
                      CONSEQUENCE · {Math.round(item.signal.evaluationImpactCp)} CP
                    </Typography>
                  )}
                  {item.signal.proofSans.length > 0 && (
                    <Typography sx={{ mt: 0.25, color: "text.secondary", fontSize: ".65rem" }}>
                      LINE · {item.signal.proofSans.join(" ")}
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <Typography
                    sx={{ color: "secondary.main", fontSize: ".6rem", fontWeight: 950, letterSpacing: ".12em" }}
                  >
                    {item.kind === "critical" ? "REVIEW MOMENT" : "VERIFIED LINE"}
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: "1rem", fontWeight: 950, letterSpacing: "-.035em" }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ mt: 0.75, color: "text.secondary", fontSize: ".72rem", lineHeight: 1.45 }}>
                    {reviewEvidenceDescription(item.kind, evidenceMove, line)}
                  </Typography>
                </>
              ),
          };
        }),
      ),
    [groups, evidenceMove, line, onPlayVerifiedLine, bookReference, displayFen],
  );
  const bookOnly = temporaryActive ? temporaryEvidence?.motifs.includes("book") : move?.motifs.includes("book");
  return bookOnly ? items.filter((item) => item.book) : items;
};

export const BoardEvidenceBadges = (props: Props) => {
  const items = useBoardEvidenceItems(props);
  const visible =
    props.revealEvidence !== false &&
    (props.temporaryActive === true ||
      !props.move ||
      props.move.isPlayerMove ||
      props.includeOpponentEvidence === true ||
      props.reviewDecision === true);
  const relationships =
    visible && (props.showTactics ?? props.showMotifs)
      ? (props.relationships ??
        relationshipPresentation(props.displayFen, {
          ...DEFAULT_REVIEW_BOARD_SETTINGS,
          showAllPositionMarkers: true,
          markerGroups: { pin: true, support: false },
        }).items)
      : [];
  const bookOnly = props.temporaryActive
    ? props.temporaryEvidence?.motifs.includes("book")
    : props.move?.motifs.includes("book");
  const previous = props.precedingMove;
  const preceding = useBoardEvidenceItems({
    ...props,
    move: null,
    displayFen: previous?.fen ?? props.displayFen,
    temporaryActive: true,
    temporaryEvidence: previous?.evidence,
    positionFacts: previous?.facts ? { fen: previous.fen, facts: previous.facts } : undefined,
    verifiedLineOverride: null,
    reviewDecision: false,
    bookReference: previous?.bookReference,
  });
  const retained: BoardEvidenceItem[] = [];
  if (previous?.displayFen === props.displayFen) {
    const before = new Chess(previous.fen);
    const current = new Chess(props.displayFen);
    for (const item of preceding) {
      const square = item.square as Parameters<Chess["get"]>[0];
      const piece = before.get(square);
      const now = current.get(square);
      if (!piece || piece.type !== now?.type || piece.color !== now.color) continue;
      retained.push({
        ...item,
        id: `preceding:${item.id}`,
        moveScope: "preceding",
        description: `Your preceding move: ${item.description}`,
        disclosure: (
          <>
            <Typography variant="overline">YOUR PRECEDING MOVE</Typography>
            {item.disclosure}
          </>
        ),
        highlightSquares: [square],
        highlightFen: props.displayFen,
      });
    }
  }
  return (
    <BoardEvidenceWheel
      items={[...mergeRelationshipEvidence(items, bookOnly ? [] : relationships), ...retained]}
      displayFen={props.displayFen}
      orientation={props.orientation}
      onActiveChange={props.onActiveChange}
    />
  );
};
